import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { NavigationEnd, Router } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AppService } from "../app.service";
import { PlanBlockedDialog, PlanBlockedData } from "../usage/plan-blocked-dialog.component";

/** Shape of `usage/connector-summary`. Every field is optional on purpose -
 *  an older backend, or a connector without usage tracking, must not read as
 *  "out of quota". */
export interface PlanSummary {
  tier?: string;
  monthlyLimit?: number;
  tokenConsumed?: number;
  tokensRemaining?: number;
  usagePercentage?: number;
  canUse?: boolean;
  memoryUsageStatus?: string;
  startPeriod?: number;
  periodEnd?: number;
  renewsOn?: string;
  info?: string;
}

/**
 * The single source of truth for "is this workspace still allowed to use the
 * product". The backend blocks writes once the monthly allowance runs out - on
 * every tier, free or paid (see UsageService.check_usage_limits) - and the UI
 * has to mirror that instead of letting the user walk into 429s screen by
 * screen.
 *
 * Blocked means: nothing but the billing, legal and auth routes is reachable.
 */
@Injectable({ providedIn: "root" })
export class PlanGateService {

  /** Routes that stay reachable while blocked. Without these the user is
   *  trapped with no way to pay, read the terms, or sign out. */
  private static readonly ALLOWED_WHILE_BLOCKED = [
    "/subscription",
    "/payments",
    "/login",
    "/story",
    "/developers",
    "/recipes",
    "/mcp-guide",
    "/terms",
    "/privacy-policy",
  ];

  /** How long a summary is trusted before the next navigation refetches it. */
  private static readonly TTL_MS = 60_000;

  private _blocked = new BehaviorSubject<boolean>(false);
  blocked$ = this._blocked.asObservable();

  summary: PlanSummary | null = null;
  reason: string | null = null;
  renewsOn: string | null = null;

  private loadedAt = 0;
  private inFlight: Promise<void> | null = null;
  private dialogRef: MatDialogRef<PlanBlockedDialog> | null = null;

  constructor(
    private appService: AppService,
    private router: Router,
    private dialog: MatDialog
  ) {
    // The dialog is an interstitial, not a wall on top of the paywall itself.
    // Once the user reaches a billing route it has done its job.
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && this.isAllowedRoute(event.urlAfterRedirects)) {
        this.closeDialog();
      }
    });
  }

  get blocked(): boolean {
    return this._blocked.value;
  }

  isAllowedRoute(url: string): boolean {
    const path = (url || "").split("?")[0].split("#").pop() || "/";
    if (path === "/" || path === "") return true;
    return PlanGateService.ALLOWED_WHILE_BLOCKED.some(
      allowed => path === allowed || path.startsWith(allowed + "/")
    );
  }

  // -------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------

  /** Resolves once the gate state is known. Only the very first call blocks -
   *  after that a stale summary is refreshed in the background so navigation
   *  never waits on the network. */
  async ensureLoaded(): Promise<void> {
    if (!this.loadedAt) {
      await this.refresh();
      return;
    }
    if (Date.now() - this.loadedAt >= PlanGateService.TTL_MS) {
      this.refresh();
    }
  }

  /** Re-reads the allowance. Concurrent callers share one request. */
  async refresh(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async load(): Promise<void> {
    const ws = this.readWorkspace();
    if (!ws) {
      // No workspace yet (fresh signup, or signed out). Nothing to gate.
      this.setBlocked(false);
      return;
    }

    try {
      const payload = { routeType: "UI_SESSION" };
      const signatureData = await this.appService.wssign(payload, ws.workspaceName);
      if (!signatureData) throw new Error("Failed to sign usage request");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        "usage/connector-summary",
        ws.apiKey,
        signature
      );

      if (response?.status !== "Success") throw new Error(response?.message);

      this.applySummary(response.data as PlanSummary);
    } catch (error) {
      // Fail open. A network hiccup must never lock a paying customer out;
      // the backend still refuses the writes that actually cost quota.
      console.error("PlanGate: could not read usage summary", error);
      this.loadedAt = Date.now();
    }
  }

  /** Re-evaluates the gate from a summary payload someone else already
   *  fetched, so a page that reads usage does not trigger a second call. */
  applySummary(data: PlanSummary | null): void {
    this.loadedAt = Date.now();

    if (!data || data.info) {
      // "Usage tracking not setup" - not a quota verdict.
      this.setBlocked(false);
      return;
    }

    this.summary = data;

    // Only signals the backend itself gates on. EXPIRED is deliberately absent:
    // it is a cached mirror of the counter, and treating it as authoritative
    // would lock out a workspace whose allowance has already refilled.
    const status = (data.memoryUsageStatus || "").toUpperCase();
    const exhausted =
      (typeof data.tokensRemaining === "number" && data.tokensRemaining <= 0) ||
      data.canUse === false ||
      status === "NOT_ACTIVE";

    if (exhausted) {
      this.renewsOn = data.renewsOn || this.renewsOn;
      if (!this.reason) {
        this.reason = status === "NOT_ACTIVE"
          ? "Your plan is no longer active."
          : "You've used every memory in this month's allowance.";
      }
    } else {
      // Allowance is back (renewal, upgrade). Drop the stale copy with it.
      this.reason = null;
      this.renewsOn = null;
    }

    this.setBlocked(exhausted);
  }

  // -------------------------------------------------------------------
  // Live signals from the API
  // -------------------------------------------------------------------

  /**
   * Called by the HTTP interceptor. The allowance can run out mid-session, and
   * the 429 is the first thing that knows it - waiting for the next summary
   * poll would let the user keep hitting a wall.
   */
  reportQuotaError(body: any): void {
    // custom_error() puts the snapshot under `details`; tolerate `data` too.
    const details = body?.details || body?.data || {};

    this.reason = details.reason || body?.message || "Monthly limit reached";
    this.renewsOn = details.renews_on || null;

    this.summary = {
      ...(this.summary || {}),
      tier: details.tier ?? this.summary?.tier,
      monthlyLimit: details.limit ?? this.summary?.monthlyLimit,
      tokenConsumed: details.tokens_used ?? this.summary?.tokenConsumed,
      tokensRemaining: details.remaining ?? 0,
    };

    this.loadedAt = Date.now();

    // The user was mid-action when the wall arrived, so say so. A block found
    // during navigation needs no popup - the guard drops them on the billing
    // page, which explains itself.
    this.setBlocked(true, true);
  }

  /** After a successful checkout, or on sign-out. */
  clear(): void {
    this.summary = null;
    this.reason = null;
    this.renewsOn = null;
    this.loadedAt = 0;
    this.setBlocked(false);
  }

  // -------------------------------------------------------------------
  // Enforcement
  // -------------------------------------------------------------------

  private setBlocked(next: boolean, notify = false): void {
    const previous = this._blocked.value;
    if (previous === next) return;

    this._blocked.next(next);

    if (!next) {
      this.closeDialog();
      return;
    }

    if (notify && !this.isAllowedRoute(this.router.url)) {
      this.openDialog();
    }
  }

  private openDialog(): void {
    if (this.dialogRef) return;

    const data: PlanBlockedData = {
      tier: this.summary?.tier || "free",
      limit: this.summary?.monthlyLimit ?? null,
      used: this.summary?.tokenConsumed ?? null,
      reason: this.reason,
      renewsOn: this.renewsOn,
    };

    this.dialogRef = this.dialog.open(PlanBlockedDialog, {
      width: "500px",
      maxWidth: "95vw",
      disableClose: true,
      hasBackdrop: true,
      data,
    });

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
      if (result === "logout") {
        this.logOut();
      } else {
        this.router.navigate(["/subscription"]);
      }
    });
  }

  private closeDialog(): void {
    if (!this.dialogRef) return;
    const ref = this.dialogRef;
    this.dialogRef = null;
    ref.close();
  }

  private async logOut(): Promise<void> {
    this.clear();
    this.appService.remove("xdbc.lastUrl");
    try {
      await this.appService.logOut();
    } catch {
      // logOut already routes to /login on its own failure paths.
    }
  }

  private readWorkspace(): { apiKey: string; workspaceName: string } | null {
    const wsJson = this.appService.get("xdbc.workspace");
    if (!wsJson || wsJson === "undefined" || wsJson.trim() === "") return null;

    try {
      const ws = JSON.parse(wsJson);
      if (!ws?.apiKey || !ws?.workspaceName) return null;
      return { apiKey: ws.apiKey, workspaceName: ws.workspaceName };
    } catch {
      return null;
    }
  }
}
